import { useEffect, useState } from 'react';
import { nextLifecycleRefresh, ServiceLifecycle, ServiceLifecycleApi } from '../Services/ServiceLifecycle';

type ScopedLifecycle = { scope: string | null; value: ServiceLifecycle };

export function useServiceLifecycle(scope: string | null = null): ServiceLifecycle | null {
  const [result, setResult] = useState<ScopedLifecycle | null>(null);
  const state = result?.scope === scope ? result.value : null;

  useEffect(() => {
    if (!state?.banner.enabled || !state.banner.expiresAt) {
      return;
    }
    const deadline = Date.now() + Date.parse(state.banner.expiresAt) - Date.parse(state.serverTime);
    let timer: ReturnType<typeof setTimeout>;
    const expire = () => {
      const remaining = deadline - Date.now();
      if (remaining > 0) {
        timer = setTimeout(expire, Math.min(remaining, 2_147_483_647));
      } else {
        setResult((current) =>
          current === result ? { scope, value: { ...state, banner: { ...state.banner, enabled: false } } } : current,
        );
      }
    };
    expire();
    return () => clearTimeout(timer);
  }, [result, scope, state]);

  useEffect(() => {
    setResult((current) => current?.scope === scope ? current : null);
    let active = true;
    const controller = new AbortController();
    let pending = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const refresh = async () => {
      if (pending) {
        return;
      }
      pending = true;
      clearTimeout(timer);
      let delay = 30_000;
      try {
        let value: ServiceLifecycle;
        if (scope === null) {
          value = await ServiceLifecycleApi.get(controller.signal);
        } else {
          try {
            value = await ServiceLifecycleApi.getScoped(controller.signal);
          } catch {
            if (!active) {
              return;
            }
            setResult((current) => current?.scope === scope ? null : current);
            value = await ServiceLifecycleApi.get(controller.signal);
          }
        }
        if (active) {
          setResult({ scope, value });
          delay = nextLifecycleRefresh(value);
        }
      } catch {
        // Public notices may survive transport failures; scoped failures clear private state.
      } finally {
        pending = false;
        if (active) {
          timer = setTimeout(() => void refresh(), delay);
        }
      }
    };

    const onFocus = () => void refresh();
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void refresh();
      }
    };
    void refresh();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      active = false;
      controller.abort();
      clearTimeout(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [scope]);

  return state;
}
